import React, { useState } from "react";
import { Form, Input, Button, message, Tabs, Card } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import api from "../utils/api";
import { setToken, setUser } from "../utils/secureStorage";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const gradientMove = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(-45deg, #f5f7fa, #c3cfe2, #e0e7ff, #f3e8ff);
  background-size: 400% 400%;
  animation: ${gradientMove} 15s ease infinite;
  z-index: 0;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 50%, rgba(118, 75, 162, 0.1) 0%, transparent 50%);
    animation: ${float} 8s ease-in-out infinite;
  }
`;

const AnimatedCard = styled(Card)`
  animation: ${slideIn} 0.6s ease-out;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.25);
  }
`;

const AnimatedTitle = styled.h2`
  animation: ${fadeIn} 0.8s ease-out;
  background: linear-gradient(135deg, #4f46e5, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 32px;
  text-shadow: 0 2px 10px rgba(102, 126, 234, 0.2);
`;

const AnimatedInput = styled(Input)`
  transition: all 0.3s ease;
  
  &:hover, &:focus {
    border-color: #8b5cf6 !important;
    box-shadow: 0 0 15px rgba(139, 92, 246, 0.3);
  }
`;

const AnimatedButton = styled(Button)`
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoginRegister = ({ onLoginSuccess, isMobile }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");

  const handleLogin = async (values) => {
    try {
      setLoading(true);
      console.log("Login request:", values);
      const response = await api.post("/auth/login", values);
      console.log("Login response:", response);
      
      if (response.data.success) {
        // Save token and user info
        setToken(response.data.token);
        setUser(response.data.user);
        message.success("登录成功");
        onLoginSuccess();
      } else {
        message.error(response.data.message || "登录失败");
      }
    } catch (error) {
      console.error("Login error:", error);
      console.error("Login error response:", error.response);
      message.error(error.response?.data?.message || "登录失败，请检查用户名和密码");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    try {
      setLoading(true);
      console.log("Register request:", values);
      const response = await api.post("/auth/register", values);
      console.log("Register response:", response);
      
      if (response.data.success) {
        // Save token and user info
        setToken(response.data.token);
        setUser(response.data.user);
        message.success("注册成功");
        onLoginSuccess();
      } else {
        message.error(response.data.message || "注册失败");
      }
    } catch (error) {
      console.error("Register error:", error);
      console.error("Register error response:", error.response);
      message.error(error.response?.data?.message || "注册失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <AnimatedBackground />
      
      <AnimatedCard
        style={{
          width: isMobile ? "85%" : "400px",
          padding: "40px 30px",
          borderRadius: "20px",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          textAlign: "center",
          zIndex: 2,
          position: "relative",
        }}
        bordered={false}
      >
        <AnimatedTitle>Sora角色库</AnimatedTitle>
        
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          style={{
            color: "#333",
          }}
          tabBarStyle={{
            marginBottom: "24px",
            borderBottom: "1px solid rgba(139, 92, 246, 0.2)",
          }}
          items={[
            {
              key: "login",
              label: "登录",
              children: (
                <Form
                  name="login"
                  onFinish={handleLogin}
                  layout="vertical"
                >
                  <Form.Item
                    name="username"
                    rules={[{ required: true, message: "请输入用户名" }]}
                  >
                    <AnimatedInput
                      prefix={<UserOutlined style={{ color: "#8b5cf6" }} />}
                      style={{
                        background: "rgba(255,255,255,0.8)",
                        borderColor: "rgba(139, 92, 246, 0.3)",
                        color: "#333",
                        borderRadius: "12px",
                        padding: "12px",
                      }}
                      placeholder="用户名"
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: "请输入密码" }]}
                  >
                    <AnimatedInput.Password
                      prefix={<LockOutlined style={{ color: "#8b5cf6" }} />}
                      style={{
                        background: "rgba(255,255,255,0.8)",
                        borderColor: "rgba(139, 92, 246, 0.3)",
                        color: "#333",
                        borderRadius: "12px",
                        padding: "12px",
                      }}
                      placeholder="密码"
                    />
                  </Form.Item>
                  
                  <Form.Item>
                    <AnimatedButton
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #4f46e5, #8b5cf6)",
                        borderColor: "transparent",
                        fontSize: "16px",
                        fontWeight: 600,
                        height: "48px",
                        color: "#fff",
                      }}
                    >
                      登录
                    </AnimatedButton>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: "register",
              label: "注册",
              children: (
                <Form
                  name="register"
                  onFinish={handleRegister}
                  layout="vertical"
                >
                  <Form.Item
                    name="username"
                    rules={[
                      { required: true, message: "请输入用户名" },
                      { min: 3, message: "用户名至少3个字符" }
                    ]}
                  >
                    <AnimatedInput
                      prefix={<UserOutlined style={{ color: "#8b5cf6" }} />}
                      style={{
                        background: "rgba(255,255,255,0.8)",
                        borderColor: "rgba(139, 92, 246, 0.3)",
                        color: "#333",
                        borderRadius: "12px",
                        padding: "12px",
                      }}
                      placeholder="用户名"
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "请输入密码" },
                      { min: 6, message: "密码至少6个字符" }
                    ]}
                  >
                    <AnimatedInput.Password
                      prefix={<LockOutlined style={{ color: "#8b5cf6" }} />}
                      style={{
                        background: "rgba(255,255,255,0.8)",
                        borderColor: "rgba(139, 92, 246, 0.3)",
                        color: "#333",
                        borderRadius: "12px",
                        padding: "12px",
                      }}
                      placeholder="密码"
                    />
                  </Form.Item>
                  
                  <Form.Item>
                    <AnimatedButton
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #4f46e5, #8b5cf6)",
                        borderColor: "transparent",
                        fontSize: "16px",
                        fontWeight: 600,
                        height: "48px",
                        color: "#fff",
                      }}
                    >
                      注册
                    </AnimatedButton>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
      </AnimatedCard>
    </div>
  );
};

export default LoginRegister;